// 画面の表示失敗(白画面)を後から調査できるようにするための診断情報。
//
// 背景: エスカ#1241(Member アプリで「今夜参加できる宣言」LPが白くなる)では、
// 1. 例外を伴わない描画失敗はどこにも記録されない
// 2. 例外ログ(ClientErrorLog)にもどのモーダル/LPを開いていたか・直前の操作が残らない
// ため、原因が特定できなかった。そこで
// - buildErrorContext: 例外ログに添える「どの画面で何を開いていたか」の文脈
// - findBlankModalSymptom: 表示から一定時間経っても中身が空のモーダルの検出
// を純粋関数として用意し、ClientDiagnosticsService から使う。

// componentProps のうち、送ってよい(個人情報を含まない)識別子だけを拾う
const MODAL_PROP_KEYS = ["kind", "from", "type", "eventName"] as const;
const MAX_PROP_LENGTH = 50;

export type ModalSummary = {
  tag: string;
  props: { [key: string]: string | number };
};

export type ErrorContext = {
  host: string;
  route: string;
  modal: ModalSummary | null;
  crumbs: string[];
  visibility: string;
  online: boolean;
};

export type BlankModalSymptom = {
  tag: string;
  props: { [key: string]: string | number };
  // ヘッダーのタイトルが空か(#1241 の報告では×だけでタイトルも出ていなかった)
  headerTitleEmpty: boolean;
  pageHeight: number;
  imgCount: number;
  imgLoadedCount: number;
};

/** 文字列からクエリ文字列を落とす(トークン等が載りうるため送信しない) */
export const stripQueryString = (value: string): string =>
  value.replace(/\?[^\s#]*/g, "");

/** ion-modal に載っている Angular コンポーネントのタグ名と識別用 props を取り出す */
export const summarizeModal = (modal: Element): ModalSummary => {
  const page = modal.querySelector(".ion-page");
  const tag = page ? page.tagName.toLowerCase() : "";
  const props: { [key: string]: string | number } = {};
  const componentProps = (modal as HTMLIonModalElement).componentProps as
    | { [key: string]: unknown }
    | undefined;
  if (componentProps) {
    for (const key of MODAL_PROP_KEYS) {
      const value = componentProps[key];
      if (typeof value === "string") {
        props[key] = value.slice(0, MAX_PROP_LENGTH);
      } else if (typeof value === "number") {
        props[key] = value;
      }
    }
  }
  return { tag, props };
};

/** 一番手前に表示中のモーダル(なければ null) */
export const findTopModal = (doc: Document): Element | null => {
  const modals = Array.from(doc.querySelectorAll("ion-modal")).filter(
    (modal) => !modal.classList.contains("overlay-hidden")
  );
  return modals.length > 0 ? modals[modals.length - 1] : null;
};

export const buildErrorContext = (
  doc: Document,
  crumbs: string[]
): ErrorContext => {
  const topModal = findTopModal(doc);
  return {
    host: doc.location?.host ?? "",
    route: stripQueryString(doc.location?.hash ?? ""),
    modal: topModal ? summarizeModal(topModal) : null,
    crumbs: [...crumbs],
    visibility: doc.visibilityState,
    online: navigator.onLine,
  };
};

/**
 * 表示済みのモーダルの中身が空(ヘッダー以外に文字も読み込み済みの画像等もない)なら症状を返す。
 * 中身があれば null。描画途中の判定にならないよう、表示から数秒待ってから呼ぶこと。
 */
export const findBlankModalSymptom = (
  modal: Element
): BlankModalSymptom | null => {
  const { tag, props } = summarizeModal(modal);
  const page = modal.querySelector(".ion-page") as HTMLElement | null;
  const header = page?.querySelector("ion-header") as HTMLElement | null;
  const headerTitle = header?.querySelector("ion-title") as HTMLElement | null;
  const headerTitleEmpty = !headerTitle || !headerTitle.innerText.trim();

  const isOutsideHeader = (el: Element) => !header || !header.contains(el);
  const imgs = page
    ? Array.from(page.querySelectorAll("img")).filter(isOutsideHeader)
    : [];
  const imgLoadedCount = imgs.filter(
    (img) => img.complete && img.naturalWidth > 0
  ).length;
  const hasOtherMedia = page
    ? Array.from(page.querySelectorAll("video, canvas, iframe")).some(
        isOutsideHeader
      )
    : false;
  const textLength = page
    ? page.innerText.trim().length - (header?.innerText.trim().length ?? 0)
    : 0;

  if (textLength > 0 || imgLoadedCount > 0 || hasOtherMedia) {
    return null;
  }
  return {
    tag,
    props,
    headerTitleEmpty,
    pageHeight: page ? Math.round(page.getBoundingClientRect().height) : 0,
    imgCount: imgs.length,
    imgLoadedCount,
  };
};
